import React, { FC, useContext, useEffect, useRef, useState } from "react";
import cx from "classnames";
import st from "./index.module.scss";
import { BodyClassContext } from "context/bodyClass";

const MusicPlayer: FC<{
  isSticky: boolean;
  audios: { [key: number]: { name: string; file: string } };
  activeAudioIdx: number;
  onClickNext: () => void;
}> = ({ isSticky, audios, activeAudioIdx, onClickNext }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const { bodyClass } = useContext(BodyClassContext);

  const isDark = !isSticky && bodyClass.includes("whitebg");

  useEffect(() => {
    if (isPlaying) {
      audioRef?.current?.play();
    } else {
      audioRef?.current?.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      audioRef?.current?.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAudioIdx]);

  return (
    <div className={cx(st.musicPlayer, isDark && st.dark)}>
      <audio
        ref={audioRef}
        src={audios[activeAudioIdx]?.file}
        muted={isMuted}
        autoPlay={false}
      ></audio>
      <div className={st.buttonControls}>
        <button
          style={{ display: isMuted ? "inline" : "none" }}
          className={st.musicMute}
          aria-label="Mute Button"
          onClick={() => setIsMuted(false)}
        >
          <svg
            width="19"
            height="23"
            viewBox="0 0 19 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_705_490)">
              <path
                d="M9.49443 23C8.08027 23 6.66611 23 5.25196 23C5.09159 23 4.93122 23.0037 4.77085 22.9814C4.3189 22.9218 4.02368 22.5979 4.03097 22.1921C4.03826 21.7975 4.31161 21.4662 4.75627 21.4475C5.68568 21.4066 6.61873 21.414 7.55178 21.4103C8.74361 21.4066 8.74361 21.4103 8.74361 20.1818C8.74361 19.7201 8.72174 19.2585 8.7509 18.7968C8.77277 18.4208 8.63427 18.2756 8.27709 18.2086C6.35631 17.8401 4.8729 16.7865 3.82322 15.1112C3.14894 14.0315 2.80269 12.8477 2.7954 11.5633C2.79176 10.8634 2.78812 10.1635 2.7954 9.46356C2.80269 8.92002 3.07605 8.60358 3.528 8.59241C3.99817 8.58124 4.30068 8.90513 4.31161 9.46356C4.32255 10.1337 4.31161 10.8038 4.31526 11.4739C4.34806 15.5356 8.4739 18.0485 11.9728 16.135C12.1223 16.0531 12.4321 16.0345 12.2972 15.7255C12.1332 15.3457 11.7687 15.1819 11.4808 15.3308C10.7227 15.7292 9.92451 15.8818 9.07164 15.8111C7.00143 15.6324 5.27018 13.7561 5.24467 11.6303C5.23009 10.5432 5.23738 9.45612 5.24467 8.36903C5.24831 8.08237 5.17177 7.86272 4.97496 7.65052C3.43688 5.96033 1.91338 4.25897 0.38259 2.56505C0.262314 2.41241 0.109235 2.28584 0.0472743 2.09597C-0.0657125 1.74602 0.0217612 1.44819 0.309695 1.22481C0.597629 1.00144 1.02042 1.0275 1.30835 1.32161C1.73114 1.74974 2.12842 2.20765 2.53298 2.6544C4.71982 5.06683 6.91031 7.47926 9.09715 9.89542C12.1441 13.2609 15.1911 16.6264 18.2345 19.9919C18.4131 20.1892 18.6063 20.3716 18.7703 20.5801C19.0874 20.9785 19.0728 21.4029 18.7484 21.697C18.4277 21.9874 17.9903 21.9725 17.6586 21.6076C16.7001 20.5689 15.7524 19.5154 14.8048 18.4655C14.4294 18.0523 14.0503 17.6353 13.6822 17.2146C13.551 17.0657 13.4271 17.0434 13.2631 17.1513C12.4357 17.7023 11.5391 18.082 10.5623 18.2459C10.3546 18.2794 10.2708 18.4059 10.2708 18.6181C10.2744 19.4223 10.278 20.2264 10.2708 21.0306C10.2671 21.3768 10.4785 21.4029 10.7336 21.4029C11.7687 21.3991 12.8038 21.3991 13.839 21.4029C14.5825 21.4066 14.9907 21.6932 14.987 22.2033C14.9834 22.7133 14.5861 22.9888 13.828 22.9925C12.3774 23 10.9341 23 9.49443 23Z"
                fill="white"
              ></path>
              <path
                d="M13.7659 7.9112C13.7659 9.08763 13.7659 10.2603 13.7659 11.4368C13.7659 11.8426 13.7112 12.2372 13.6019 12.6244C13.4889 13.0302 13.4087 13.0525 13.1353 12.7547C12.0419 11.5596 10.9558 10.3609 9.86602 9.15837C8.45186 7.59848 7.045 6.03114 5.61626 4.48242C5.32468 4.16597 5.24085 3.86442 5.32833 3.44373C5.80214 1.23606 7.75572 -0.197251 10.0264 0.0223997C12.0346 0.219713 13.7222 2.11839 13.7586 4.2516C13.7805 5.47271 13.7659 6.69009 13.7659 7.9112Z"
                fill="white"
              ></path>
              <path
                d="M16.2157 10.6512C16.2849 11.9951 16.1027 13.2907 15.5159 14.5081C15.2608 15.0367 15.2535 15.0144 14.8343 14.6384C14.3277 14.1842 14.0835 13.7449 14.4443 13.045C14.6849 12.5759 14.6922 12.0137 14.6958 11.4739C14.6995 10.7591 14.6849 10.0481 14.7068 9.33327C14.7213 8.8828 15.0202 8.60358 15.4248 8.58869C15.8476 8.5738 16.1647 8.86046 16.2084 9.31465C16.2303 9.5343 16.2193 9.7614 16.2193 9.98477C16.2157 10.2044 16.2157 10.4278 16.2157 10.6512Z"
                fill="white"
              ></path>
            </g>
            <defs>
              <clipPath id="clip0_705_490">
                <rect width="19" height="23" fill="white"></rect>
              </clipPath>
            </defs>
          </svg>
        </button>
        <button
          style={{ display: !isMuted ? "inline" : "none" }}
          aria-label="Unmute Button"
          onClick={() => setIsMuted(true)}
        >
          <svg
            width="14"
            height="23"
            viewBox="0 0 14 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_705_472)">
              <path
                d="M11.4365 7.92707C11.4365 9.10255 11.4441 10.2743 11.4365 11.4498C11.4213 13.8937 9.44628 15.843 6.99647 15.8467C4.59223 15.8467 2.57921 13.8751 2.56022 11.4795C2.54503 9.10255 2.54123 6.72183 2.56022 4.34483C2.57921 1.93806 4.56185 0.0186081 7.01166 8.70218e-06C9.41589 -0.0185907 11.4175 1.94922 11.4365 4.3597C11.4441 5.55007 11.4365 6.73671 11.4365 7.92707Z"
                fill="white"
              ></path>
              <path
                d="M7.03788 23C5.549 23 4.06012 23.0037 2.57125 22.9963C2.34336 22.9963 2.11167 22.9851 1.89517 22.9331C1.51536 22.8401 1.29127 22.5797 1.29886 22.1891C1.30266 21.8208 1.51916 21.5716 1.87998 21.4786C2.09648 21.4228 2.33196 21.4116 2.55605 21.4079C3.60434 21.4005 4.65263 21.393 5.70092 21.4079C6.06555 21.4116 6.22127 21.2963 6.20988 20.9206C6.18709 20.1915 6.19089 19.4624 6.20988 18.7333C6.21747 18.4208 6.10353 18.272 5.78828 18.22C2.63202 17.7066 -0.0874633 14.9353 0.00369237 11.1782C0.018885 10.6128 -0.00390394 10.0474 0.00749053 9.48198C0.018885 8.92771 0.33793 8.58921 0.812699 8.59665C1.29886 8.60409 1.56853 8.92399 1.59132 9.50058C1.6293 10.5682 1.49257 11.6395 1.73945 12.6997C2.31297 15.1362 4.50451 16.7915 7.13663 16.7581C9.63961 16.7246 11.8046 14.9651 12.2945 12.5732C12.4882 11.6283 12.3743 10.6761 12.3971 9.72377C12.4161 8.96119 12.6553 8.59665 13.1871 8.59293C13.7378 8.58921 13.9961 8.93143 13.9809 9.70145C13.9581 10.7839 14.1024 11.8776 13.8214 12.9452C13.0693 15.7872 11.2348 17.5578 8.30266 18.1976C7.92285 18.2795 7.75952 18.406 7.77852 18.8077C7.8127 19.4922 7.8127 20.1766 7.77852 20.8611C7.75573 21.3075 7.94184 21.4191 8.36343 21.4116C9.38134 21.3893 10.3992 21.4005 11.4171 21.4079C11.6298 21.4079 11.8425 21.4191 12.0514 21.46C12.4654 21.5381 12.6971 21.8022 12.6933 22.2077C12.6933 22.6131 12.4502 22.8735 12.0438 22.9554C11.8539 22.9926 11.6526 22.9963 11.4551 22.9963C9.98524 23 8.51156 23 7.03788 23Z"
                fill="white"
              ></path>
            </g>
            <defs>
              <clipPath id="clip0_705_472">
                <rect width="14" height="23" fill="white"></rect>
              </clipPath>
            </defs>
          </svg>
        </button>
        <button
          style={{ display: !isPlaying ? "inline" : "none" }}
          aria-label="Music Play Button"
          onClick={() => setIsPlaying(true)}
        >
          <svg
            width="21"
            height="25"
            viewBox="0 0 21 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.356934 0.935791V24.0297L20.0547 12.4828L0.356934 0.935791Z"
              fill="white"
            ></path>
          </svg>
        </button>
        <button
          style={{ display: !isPlaying ? "none" : "inline" }}
          className={st.musicStop}
          aria-label="Music Stop Button"
          onClick={() => setIsPlaying(false)}
        >
          <svg
            width="20"
            height="25"
            viewBox="0 0 20 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.48877"
              y="0.935547"
              width="7.43359"
              height="23.0939"
              fill="white"
            ></rect>
            <rect
              x="12.4648"
              y="0.935547"
              width="7.43359"
              height="23.0939"
              fill="white"
            ></rect>
          </svg>
        </button>
        <button
          onClick={() => onClickNext && onClickNext()}
          aria-label="Music Next Button"
        >
          <svg
            width="34"
            height="25"
            viewBox="0 0 34 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.7749 0.935791V24.0297L33.4727 12.4828L13.7749 0.935791Z"
              fill="white"
            ></path>
            <path
              d="M0.946289 0.935791V24.0297L20.644 12.4828L0.946289 0.935791Z"
              fill="white"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MusicPlayer;
